import { useTranslate } from 'module/hooks/use-translation';
import { Loading } from 'module/ui/components/atoms/loading';
import { toHHMMSS } from 'module/ui/core/time-covertor';
import { faVideo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@rocketlane/ui';
import { useQuery } from '@tanstack/react-query';
import { CitationAPI } from 'api/citation';
import { CitationType } from 'api/citation/types';
import { CitationObject, CitationResponse } from 'queries/citation';
import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  Label,
  CitationDataPopoverContainer,
  StyledLoader,
  SummaryContainer,
  SummaryLabel,
  SummaryText,
  CitationDataWrapper,
} from './shared-styles';
import { OnViewCitationSourceCallback } from '../types';

interface CitationDataPopoverProps {
  citationId: number;
  onViewCitationSource?: OnViewCitationSourceCallback;
}

const CitationTimeStampWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  font-size: var(--font-size-small);
  color: var(--text-secondary);
`;

const CitationTimeStamp = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

const CitationDataContainer = styled.div<{ $hasGreyBackground?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: 0 var(--spacing-05);
  background-color: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? 'var(--scarlet-gray-30)' : 'transparent'};
  border-radius: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? '8px' : '0'};
`;

const StyledCitationContent = styled.p`
  font-style: italic;
`;

export const CitationMeetingPagePopover: React.FC<CitationDataPopoverProps> = ({
  citationId,
  onViewCitationSource,
}) => {
  const t = useTranslate();
  const [citationData, setCitationData] = useState<CitationObject[]>([]);
  const { data, isFetching, isSuccess, isError } = useQuery<
    CitationResponse,
    Error
  >({
    queryKey: ['rl-citation', citationId],
    queryFn: () => CitationAPI.getCitation(citationId),
  });

  useEffect(() => {
    if (isSuccess || isError) {
      setCitationData(data?.metaData?.transcriptTextBlocks ?? []);
    }
  }, [data, isSuccess, isError]);

  const hasSummary = !!data?.metaData?.summary;
  const isEmail = data?.citationType === 'EMAIL';
  const summaryLabel = isEmail
    ? t('citation.email_summary')
    : t('citation.transcript_summary');

  return (
    <>
      {isFetching ? (
        <StyledLoader>
          <Loading />
        </StyledLoader>
      ) : (
        <CitationDataPopoverContainer>
          {data?.metaData?.source?.meetingName && (
            <Label>{data?.metaData?.source?.meetingName}</Label>
          )}

          {hasSummary && (
            <SummaryContainer
              $hasMarginTop={!data?.metaData?.source?.meetingName}
            >
              <SummaryLabel>{summaryLabel}</SummaryLabel>
              <SummaryText>{data?.metaData?.summary}</SummaryText>
            </SummaryContainer>
          )}

          <CitationDataWrapper $hasGreyBackground={hasSummary}>
            {citationData?.map((citation, index) => (
              <CitationDataContainer key={index} className="relative">
                {citation.fromTime && citation.toTime && (
                  <>
                    <CitationTimeStampWrapper>
                      <FontAwesomeIcon icon={faVideo} size="sm" />
                      <CitationTimeStamp>
                        {toHHMMSS(parseInt(citation.fromTime ?? '0'))} -{' '}
                        {toHHMMSS(parseInt(citation.toTime ?? '0'))}
                      </CitationTimeStamp>
                    </CitationTimeStampWrapper>
                    {onViewCitationSource && (
                      <Button
                        variant="ghost"
                        size="small"
                        className="absolute right-[var(--spacing-05)] top-0"
                        onClick={() => {
                          if (!data) {
                            return;
                          }
                          if (data.citationType === CitationType.Meeting) {
                            onViewCitationSource?.({
                              citationType: CitationType.Meeting,
                              citation: data,
                              currentBlock: citation,
                            });
                          }
                        }}
                      >
                        <FormattedMessage id="view" />
                      </Button>
                    )}
                  </>
                )}

                <strong>{citation.speaker}</strong>
                <StyledCitationContent>{`"${citation.content}"`}</StyledCitationContent>
              </CitationDataContainer>
            ))}
          </CitationDataWrapper>
        </CitationDataPopoverContainer>
      )}
    </>
  );
};
